<template>
  <section class="py-20 bg-neutral-900">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

      <!-- Header -->
      <div class="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12">
        <div>
          <p class="text-primary-400 text-sm font-semibold uppercase tracking-widest mb-2">Client Stories</p>
          <h2 class="text-3xl md:text-4xl font-bold text-white leading-tight">
            What Our Clients Say
          </h2>
        </div>
        <button
          @click="showModal = true"
          class="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold px-5 py-3 rounded-xl transition-colors shadow-lg flex-shrink-0"
        >
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          Share Your Story
        </button>
      </div>

      <!-- Loading skeleton -->
      <div v-if="isLoading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div v-for="i in 6" :key="i" class="bg-white/5 rounded-2xl p-7 animate-pulse">
          <div class="flex gap-1 mb-4">
            <div v-for="s in 5" :key="s" class="w-4 h-4 bg-white/10 rounded"></div>
          </div>
          <div class="space-y-2 mb-6">
            <div class="h-3 bg-white/10 rounded w-full"></div>
            <div class="h-3 bg-white/10 rounded w-4/5"></div>
            <div class="h-3 bg-white/10 rounded w-3/5"></div>
          </div>
          <div class="flex items-center gap-3 pt-5 border-t border-white/10">
            <div class="w-10 h-10 rounded-full bg-white/10"></div>
            <div class="space-y-1.5">
              <div class="h-3 bg-white/10 rounded w-24"></div>
              <div class="h-2.5 bg-white/10 rounded w-32"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Reviews grid -->
      <div v-else-if="reviews.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div
          v-for="review in reviews"
          :key="review.id"
          class="bg-white/5 border border-white/10 rounded-2xl p-7 flex flex-col hover:bg-white/8 hover:border-white/20 transition-all duration-300"
        >
          <!-- Stars -->
          <div class="flex gap-0.5 mb-4">
            <svg
              v-for="n in 5" :key="n"
              class="w-4 h-4 transition-colors"
              :class="n <= review.rating ? 'text-yellow-400' : 'text-white/20'"
              fill="currentColor" viewBox="0 0 20 20"
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          </div>

          <!-- Title -->
          <p v-if="review.title" class="text-white font-semibold text-sm mb-2">{{ review.title }}</p>

          <!-- Message -->
          <p class="text-neutral-400 text-sm leading-relaxed flex-1 italic">"{{ review.message }}"</p>

          <!-- Footer -->
          <div class="flex items-center gap-3 mt-6 pt-5 border-t border-white/10">
            <!-- Gradient avatar -->
            <div
              class="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
              :style="{ background: avatarGradient(review.id) }"
            >
              {{ nameInitials(review.name) }}
            </div>
            <div class="min-w-0">
              <p class="font-bold text-white text-sm truncate">{{ review.name }}</p>
              <p v-if="review.detail" class="text-xs text-neutral-500 truncate mt-0.5">{{ review.detail }}</p>
              <p class="text-xs text-neutral-600 mt-0.5">{{ formatDate(review.created_at) }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div v-else class="text-center py-16">
        <div class="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg class="w-8 h-8 text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        </div>
        <p class="text-neutral-400 text-base mb-2">No reviews yet</p>
        <p class="text-neutral-600 text-sm">Be the first to share your experience with Estate Rwanda.</p>
      </div>

      <!-- Average rating bar (shows when there are reviews) -->
      <div v-if="!isLoading && reviews.length > 0" class="mt-10 flex flex-col sm:flex-row items-center justify-center gap-6">
        <div class="text-center">
          <div class="text-5xl font-extrabold text-white leading-none">{{ averageRating }}</div>
          <div class="flex justify-center gap-0.5 mt-2">
            <svg v-for="n in 5" :key="n" class="w-4 h-4" :class="n <= Math.round(+averageRating) ? 'text-yellow-400' : 'text-white/20'" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          </div>
          <p class="text-neutral-500 text-xs mt-1">{{ total }} review{{ total !== 1 ? 's' : '' }}</p>
        </div>
      </div>

    </div>

    <!-- ── Submit Review Modal ── -->
    <transition name="modal-fade">
      <div
        v-if="showModal"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
        @click.self="closeModal"
      >
        <div class="absolute inset-0 bg-black/70 backdrop-blur-sm"></div>

        <div class="relative bg-white rounded-3xl shadow-2xl w-full max-w-lg mx-auto overflow-hidden">

          <!-- Modal header -->
          <div class="bg-gradient-to-r from-primary-500 to-primary-600 px-8 py-6">
            <div class="flex items-center justify-between">
              <div>
                <h3 class="text-xl font-bold text-white">Share Your Experience</h3>
                <p class="text-primary-100 text-sm mt-0.5">Help others find their perfect property</p>
              </div>
              <button @click="closeModal" class="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white transition-colors">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <!-- Success state -->
          <div v-if="submitSuccess" class="px-8 py-12 text-center">
            <div class="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h4 class="text-xl font-bold text-neutral-800 mb-2">Thank You!</h4>
            <p class="text-neutral-500 text-sm">Your review is now live and visible to everyone.</p>
          </div>

          <!-- Form -->
          <form v-else @submit.prevent="submitReview" class="px-8 py-6 space-y-5">

            <!-- Star rating picker -->
            <div>
              <label class="block text-sm font-semibold text-neutral-700 mb-2">
                Your Rating <span class="text-red-500">*</span>
              </label>
              <div class="flex items-center gap-1">
                <button
                  v-for="n in 5"
                  :key="n"
                  type="button"
                  @click="form.rating = n"
                  @mouseover="hoverRating = n"
                  @mouseleave="hoverRating = 0"
                  class="transition-transform hover:scale-110"
                >
                  <svg
                    class="w-9 h-9 transition-colors"
                    :class="n <= (hoverRating || form.rating) ? 'text-yellow-400' : 'text-neutral-200'"
                    fill="currentColor" viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                </button>
                <span class="ml-2 text-sm text-neutral-500">
                  {{ ratingLabel }}
                </span>
              </div>
              <p v-if="errors.rating" class="text-red-500 text-xs mt-1">{{ errors.rating }}</p>
            </div>

            <!-- Name -->
            <div>
              <label class="block text-sm font-semibold text-neutral-700 mb-1.5">
                Your Name <span class="text-red-500">*</span>
              </label>
              <input
                v-model="form.name"
                type="text"
                placeholder="e.g. Alice Mukamana"
                class="w-full px-4 py-3 border border-neutral-200 rounded-xl text-sm text-neutral-700 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
              />
              <p v-if="errors.name" class="text-red-500 text-xs mt-1">{{ errors.name }}</p>
            </div>

            <!-- Review title -->
            <div>
              <label class="block text-sm font-semibold text-neutral-700 mb-1.5">
                Review Title <span class="text-neutral-400 font-normal">(optional)</span>
              </label>
              <input
                v-model="form.title"
                type="text"
                placeholder="e.g. Excellent service, highly recommend!"
                class="w-full px-4 py-3 border border-neutral-200 rounded-xl text-sm text-neutral-700 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
              />
            </div>

            <!-- Message -->
            <div>
              <label class="block text-sm font-semibold text-neutral-700 mb-1.5">
                Your Review <span class="text-red-500">*</span>
              </label>
              <textarea
                v-model="form.message"
                rows="4"
                placeholder="Tell us about your experience with Estate Rwanda..."
                class="w-full px-4 py-3 border border-neutral-200 rounded-xl text-sm text-neutral-700 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition resize-none"
              ></textarea>
              <p v-if="errors.message" class="text-red-500 text-xs mt-1">{{ errors.message }}</p>
            </div>

            <!-- Detail -->
            <div>
              <label class="block text-sm font-semibold text-neutral-700 mb-1.5">
                Transaction Detail <span class="text-neutral-400 font-normal">(optional)</span>
              </label>
              <input
                v-model="form.detail"
                type="text"
                placeholder="e.g. Bought an Apartment in Kiyovu, Kigali"
                class="w-full px-4 py-3 border border-neutral-200 rounded-xl text-sm text-neutral-700 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
              />
            </div>

            <!-- Actions -->
            <div class="flex gap-3 pt-2">
              <button
                type="button"
                @click="closeModal"
                class="flex-1 px-5 py-3 border border-neutral-200 text-neutral-600 text-sm font-semibold rounded-xl hover:bg-neutral-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                :disabled="isSubmitting"
                class="flex-1 px-5 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white text-sm font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                <svg v-if="isSubmitting" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
                {{ isSubmitting ? 'Submitting...' : 'Publish Review' }}
              </button>
            </div>

          </form>

        </div>
      </div>
    </transition>

  </section>
</template>

<script lang="ts">
/* eslint-disable camelcase */
import { Options, Vue } from 'vue-class-component'
import { reviewsApi, Review } from '@/api/reviews'

@Options({})
export default class ReviewsSection extends Vue {
  reviews: Review[] = []
  total = 0
  isLoading = true

  showModal = false
  isSubmitting = false
  submitSuccess = false
  hoverRating = 0

  form = {
    name: '',
    rating: 0,
    title: '',
    message: '',
    detail: ''
  }

  errors: Record<string, string> = {}

  async mounted () {
    await this.loadReviews()
  }

  async loadReviews () {
    this.isLoading = true
    try {
      const res = await reviewsApi.list({ limit: 50 })
      this.reviews = res.data.data
      this.total = res.data.total
    } catch {
      this.reviews = []
    } finally {
      this.isLoading = false
    }
  }

  get averageRating (): string {
    if (this.reviews.length === 0) return '0.0'
    const avg = this.reviews.reduce((sum, r) => sum + r.rating, 0) / this.reviews.length
    return avg.toFixed(1)
  }

  get ratingLabel (): string {
    const active = this.hoverRating || this.form.rating
    const labels: Record<number, string> = { 1: 'Poor', 2: 'Fair', 3: 'Good', 4: 'Very Good', 5: 'Excellent' }
    return labels[active] ?? 'Select a rating'
  }

  validate (): boolean {
    this.errors = {}
    if (!this.form.name.trim()) this.errors.name = 'Name is required.'
    if (!this.form.rating) this.errors.rating = 'Please select a rating.'
    if (!this.form.message.trim()) this.errors.message = 'Review message is required.'
    return Object.keys(this.errors).length === 0
  }

  async submitReview () {
    if (!this.validate()) return
    this.isSubmitting = true
    try {
      const res = await reviewsApi.create({
        name: this.form.name.trim(),
        rating: this.form.rating,
        title: this.form.title.trim() || undefined,
        message: this.form.message.trim(),
        detail: this.form.detail.trim() || undefined
      })
      // Prepend the new review to the top of the list
      this.reviews.unshift(res.data)
      this.total++
      this.submitSuccess = true
      setTimeout(() => { this.closeModal() }, 2200)
    } catch {
      this.errors.message = 'Something went wrong. Please try again.'
    } finally {
      this.isSubmitting = false
    }
  }

  closeModal () {
    this.showModal = false
    setTimeout(() => {
      this.submitSuccess = false
      this.form = { name: '', rating: 0, title: '', message: '', detail: '' }
      this.errors = {}
      this.hoverRating = 0
    }, 300)
  }

  nameInitials (name: string): string {
    return name.split(' ').filter(Boolean).slice(0, 2).map(n => n[0]).join('').toUpperCase()
  }

  avatarGradient (id: number): string {
    const g = [
      'linear-gradient(135deg,#0ea5e9,#6366f1)',
      'linear-gradient(135deg,#8b5cf6,#ec4899)',
      'linear-gradient(135deg,#f59e0b,#ef4444)',
      'linear-gradient(135deg,#10b981,#0ea5e9)',
      'linear-gradient(135deg,#6366f1,#8b5cf6)',
      'linear-gradient(135deg,#f97316,#f59e0b)'
    ]
    return g[id % g.length]
  }

  formatDate (iso: string): string {
    try {
      return new Date(iso).toLocaleDateString('en-RW', { year: 'numeric', month: 'short', day: 'numeric' })
    } catch {
      return ''
    }
  }
}
</script>

<style scoped>
.modal-fade-enter-active, .modal-fade-leave-active {
  transition: opacity 0.25s ease;
}
.modal-fade-enter-from, .modal-fade-leave-to {
  opacity: 0;
}
</style>
